import Address from "components/Address"
import AmbientZoomImage from "components/AmbientZoomImage"
import PlatformBadge from "components/PlatformBadge"
import { getMarketplaceWorkMediaUrls, MarketplaceWork } from "data/marketplaceWorks"
import { getMarketplaceWorkPlatformBadge } from "data/platforms"
import useMarketplaceWorkChainData from "hooks/useMarketplaceWorkChainData"
import { ArtBlocksChainId } from "contractConfig"
import usePageMeta from "hooks/usePageMeta"
import useWindowSize from "hooks/useWindowSize"
import {
  getBlockExplorerName,
  getBlockExplorerTokenUrl,
  getOpenSeaAssetUrl,
} from "utils/artBlocksUrls"
import { getArtworkFramePadding } from "utils/galleryLayout"

interface Props {
  work: MarketplaceWork
}

const MAINNET_CHAIN_ID: ArtBlocksChainId = 1

const MarketplaceTokenDetails = ({ work }: Props) => {
  const windowSize = useWindowSize()
  const chainData = useMarketplaceWorkChainData(work)
  const mediaUrls = getMarketplaceWorkMediaUrls(work)
  usePageMeta(
    `${work.title} — r4v3n`,
    work.description,
    mediaUrls[0],
    `/token/${work.contractAddress}/${work.tokenId}`,
  )
  const artFramePadding = getArtworkFramePadding(windowSize.width)
  const artWidth = Math.max(
    180,
    windowSize.width >= 1200
      ? (1200 - 48) * 0.6 - artFramePadding
      : windowSize.width >= 768
      ? (windowSize.width - 48) * 0.6 - artFramePadding
      : windowSize.width >= 640
      ? windowSize.width - 48 - artFramePadding
      : windowSize.width - 40 - artFramePadding
  )
  const safeAspectRatio = work.aspectRatio > 0 ? work.aspectRatio : 1
  const maxArtHeight = windowSize.height > 0 ? windowSize.height * 0.78 : Number.POSITIVE_INFINITY
  let displayWidth = artWidth
  let displayHeight = displayWidth / safeAspectRatio

  if (displayHeight > maxArtHeight) {
    displayHeight = maxArtHeight
    displayWidth = displayHeight * safeAspectRatio
  }

  const tokenUrlParams = {
    chainId: work.chainId ?? MAINNET_CHAIN_ID,
    contractAddress: work.contractAddress,
    tokenId: work.tokenId,
  }
  const blockExplorerHref = getBlockExplorerTokenUrl(tokenUrlParams)
  const openSeaHref = getOpenSeaAssetUrl(tokenUrlParams)

  return (
    <div>
      <nav className="flex items-center gap-2 mb-10" aria-label="Breadcrumb">
        <a href="/projects" className="gallery-label text-gallery-muted hover:text-gallery-text transition-colors">
          Work
        </a>
        <span className="text-gallery-border">·</span>
        <span className="gallery-label text-gallery-text">{work.title}</span>
      </nav>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
        <div className="md:col-span-2">
          <div className="gallery-art-shell">
            <div className="gallery-art-frame">
              {work.mediaType === "video" ? (
                <div className="flex items-center justify-center bg-gallery-surface" style={{ width: displayWidth, height: displayHeight }}>
                  <video
                    src={mediaUrls[0]}
                    autoPlay
                    muted
                    loop
                    playsInline
                    className="h-full w-full object-contain"
                  />
                </div>
              ) : (
                <AmbientZoomImage
                  src={mediaUrls[0]}
                  fallbackSrcs={mediaUrls.slice(1)}
                  fallback={
                    <div className="flex h-full w-full flex-col items-center justify-center gap-3 px-6 text-center">
                      <span className="gallery-label text-gallery-muted">{work.platform} 1/1</span>
                      <span className="font-serif text-3xl leading-tight text-gallery-text">{work.title}</span>
                      <span className="text-sm leading-relaxed text-gallery-muted">{work.medium}</span>
                    </div>
                  }
                  alt={work.title}
                  width={displayWidth}
                  height={displayHeight}
                  eager
                />
              )}
            </div>
          </div>

          {chainData.ownerAddress && (
            <p className="mt-4 border-t border-gallery-border pt-4 text-sm text-gallery-muted">
              Owned by <Address address={chainData.ownerAddress} />
            </p>
          )}
        </div>

        <aside>
          <p className="gallery-label text-gallery-muted mb-3">{work.year}</p>
          <h1 className="font-serif text-4xl md:text-5xl leading-tight font-normal mb-3">
            {work.title}
          </h1>
          <p className="artist-name text-gallery-muted mb-6">{work.artistName}</p>

          {(() => {
            const badge = getMarketplaceWorkPlatformBadge(work)
            return badge ? (
              <div className="mb-8">
                <PlatformBadge badge={badge} />
              </div>
            ) : null
          })()}

          <dl className="gallery-meta-grid mb-8">
            <div className="gallery-meta-cell col-span-2">
              <dt>Token ID</dt>
              <dd>{work.tokenId}</dd>
            </div>
            <div className="gallery-meta-cell">
              <dt>Edition</dt>
              <dd>1/1</dd>
            </div>
            <div className="gallery-meta-cell">
              <dt>Aspect</dt>
              <dd>{safeAspectRatio.toFixed(2)}</dd>
            </div>
            <div className="gallery-meta-cell col-span-2">
              <dt>Platform</dt>
              <dd>{work.platform}</dd>
            </div>
            <div className="gallery-meta-cell col-span-2">
              <dt>Contract</dt>
              <dd className="break-all">{work.contractAddress}</dd>
            </div>
          </dl>

          <div className="mb-8 flex flex-col items-start gap-3">
            <a href={work.externalUrl} target="_blank" rel="noopener noreferrer" className="gallery-label text-gallery-muted hover:text-gallery-text transition-colors">
              {work.sourceLabel}
            </a>
            <a href={blockExplorerHref} target="_blank" rel="noopener noreferrer" className="gallery-label text-gallery-muted hover:text-gallery-text transition-colors">
              {getBlockExplorerName(tokenUrlParams.chainId)}
            </a>
            <a href={openSeaHref} target="_blank" rel="noopener noreferrer" className="gallery-label text-gallery-muted hover:text-gallery-text transition-colors">
              OpenSea
            </a>
          </div>

          <section className="mb-8">
            <h2 className="gallery-label text-gallery-muted mb-3">Medium</h2>
            <p className="text-sm leading-relaxed text-gallery-muted">{work.medium}</p>
          </section>

          <section className="mb-8">
            <h2 className="gallery-label text-gallery-muted mb-3">About</h2>
            <p className="text-sm leading-relaxed text-gallery-muted">{work.description}</p>
          </section>
        </aside>
      </div>
    </div>
  )
}

export default MarketplaceTokenDetails
